import { Command } from "cmdk";
import * as D from "@radix-ui/react-dialog";
import { useNavigate } from "@tanstack/react-router";
import { Search, Sun, Moon, Monitor, Rows3, Rows2, Rows4, PanelRight, PanelBottom, Square, PanelLeft, Keyboard } from "lucide-react";
import { Kbd } from "@dfws/ui";
import { updateSettings, useSettings } from "../settings/store";
import { STANDARD_FOLDERS, OTHER_APPS } from "../app/nav";
import { SHORTCUTS } from "./shortcuts";
import s from "./CommandPalette.module.css";

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialQuery: string;
  onShowHelp: () => void;
  onToggleSidebar: () => void;
}

export function CommandPalette({ open, onOpenChange, initialQuery, onShowHelp, onToggleSidebar }: CommandPaletteProps) {
  const navigate = useNavigate();
  const settings = useSettings();
  const run = (fn: () => void) => () => {
    onOpenChange(false);
    fn();
  };
  const shortcutFor: Record<string, readonly string[]> = {
    "/mail/inbox": SHORTCUTS.goInbox.display,
    "/mail/drafts": SHORTCUTS.goDrafts.display,
    "/mail/sent": SHORTCUTS.goSent.display,
    "/calendar": SHORTCUTS.goCalendar.display,
    "/contacts": SHORTCUTS.goContacts.display,
    "/settings": SHORTCUTS.goSettings.display,
  };
  const keys = (to: string) => shortcutFor[to]?.map((k) => <Kbd key={k}>{k}</Kbd>);

  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className={s.overlay} />
        <D.Content className={s.content} aria-describedby={undefined}>
          <D.Title className="df-visually-hidden">Command palette</D.Title>
          <Command label="Command palette" loop defaultValue="" className={s.command}>
            <div className={s.inputRow}>
              <Search aria-hidden />
              <Command.Input autoFocus defaultValue={initialQuery} placeholder="Type a command or search" className={s.input} />
              <Kbd>Esc</Kbd>
            </div>
            <Command.List className={s.list}>
              <Command.Empty className={s.empty}>No matching commands. Mail search arrives in Phase 4.</Command.Empty>
              <Command.Group heading="Go to" className={s.group}>
                {STANDARD_FOLDERS.map((f) => (
                  <Command.Item key={f.slug} value={`Go to ${f.label}`} onSelect={run(() => navigate({ to: "/mail/$folder", params: { folder: f.slug } }))} className={s.item}>
                    <f.icon aria-hidden />
                    <span>{f.label}</span>
                    <span className={s.keys}>{keys(`/mail/${f.slug}`)}</span>
                  </Command.Item>
                ))}
                {OTHER_APPS.map((a) => (
                  <Command.Item key={a.to} value={`Go to ${a.label}`} onSelect={run(() => navigate({ to: a.to }))} className={s.item}>
                    <a.icon aria-hidden />
                    <span>{a.label}</span>
                    <span className={s.keys}>{keys(a.to)}</span>
                  </Command.Item>
                ))}
              </Command.Group>
              <Command.Group heading="Appearance" className={s.group}>
                <Item icon={<Sun />} label="Light theme" active={settings.theme === "light"} onSelect={run(() => updateSettings({ theme: "light" }))} />
                <Item icon={<Moon />} label="Dark theme" active={settings.theme === "dark"} onSelect={run(() => updateSettings({ theme: "dark" }))} />
                <Item icon={<Monitor />} label="Match system theme" active={settings.theme === "system"} onSelect={run(() => updateSettings({ theme: "system" }))} />
                <Item icon={<Rows2 />} label="Comfortable density" active={settings.density === "comfortable"} onSelect={run(() => updateSettings({ density: "comfortable" }))} />
                <Item icon={<Rows3 />} label="Cozy density" active={settings.density === "cozy"} onSelect={run(() => updateSettings({ density: "cozy" }))} />
                <Item icon={<Rows4 />} label="Compact density" active={settings.density === "compact"} onSelect={run(() => updateSettings({ density: "compact" }))} />
                <Item icon={<PanelRight />} label="Reading pane on the right" active={settings.splitView === "right"} onSelect={run(() => updateSettings({ splitView: "right" }))} />
                <Item icon={<PanelBottom />} label="Reading pane below" active={settings.splitView === "bottom"} onSelect={run(() => updateSettings({ splitView: "bottom" }))} />
                <Item icon={<Square />} label="No reading pane" active={settings.splitView === "none"} onSelect={run(() => updateSettings({ splitView: "none" }))} />
                <Item icon={<PanelLeft />} label="Collapse or expand sidebar" onSelect={run(onToggleSidebar)} shortcut={SHORTCUTS.toggleSidebar.display} />
              </Command.Group>
              <Command.Group heading="Help" className={s.group}>
                <Item icon={<Keyboard />} label="Keyboard shortcuts" onSelect={run(onShowHelp)} shortcut={SHORTCUTS.help.display} />
              </Command.Group>
            </Command.List>
          </Command>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

function Item({ icon, label, onSelect, active, shortcut }: { icon: React.ReactNode; label: string; onSelect: () => void; active?: boolean; shortcut?: readonly string[] }) {
  return (
    <Command.Item value={label} onSelect={onSelect} className={s.item}>
      {icon}
      <span>{label}</span>
      {active && <span className={s.current}>Current</span>}
      {shortcut && <span className={s.keys}>{shortcut.map((k) => <Kbd key={k}>{k}</Kbd>)}</span>}
    </Command.Item>
  );
}
